import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Chip, Tooltip, makeStyles } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import SettingsInputAntennaIcon from '@material-ui/icons/SettingsInputAntenna';
import { spassuCodeApiRef } from '../../api';
import { WorkerStatus } from '../../api/types';

const POLL_INTERVAL_MS = 15_000;

const useStyles = makeStyles(theme => ({
  chip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    marginRight: theme.spacing(1),
  },
  connected: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  disconnected: {
    backgroundColor: theme.palette.grey[400],
    color: '#fff',
  },
  outdated: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
}));

export const WorkerStatusIndicator = () => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const [status, setStatus] = useState<WorkerStatus>({ connected: false });

  const fetchStatus = useCallback(async () => {
    try {
      const result = await api.getWorkerStatus();
      setStatus(result);
    } catch {
      setStatus({ connected: false });
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (status.connected && status.outdated) {
    const outdatedTooltip = `Worker v${
      status.workerVersion || '?'
    } está desatualizado. Execute "yarn worker:update" para atualizar.`;
    return (
      <Tooltip title={outdatedTooltip} arrow>
        <Chip
          icon={<WarningIcon style={{ color: 'inherit' }} />}
          label="Worker Desatualizado"
          size="small"
          className={`${classes.chip} ${classes.outdated}`}
          data-testid="worker-outdated"
        />
      </Tooltip>
    );
  }

  const label = status.connected ? 'Conectado' : 'Não Conectado';
  const tooltipText = status.connected
    ? `Worker v${status.workerVersion || '?'} conectado desde ${
        status.connectedAt
          ? new Date(status.connectedAt).toLocaleString('pt-BR')
          : '?'
      }`
    : 'Nenhum worker local conectado. Verifique se o Frontier Worker está rodando e conectado ao Backstage.';

  return (
    <Tooltip title={tooltipText} arrow>
      <Chip
        icon={<SettingsInputAntennaIcon style={{ color: 'inherit' }} />}
        label={label}
        size="small"
        className={`${classes.chip} ${
          status.connected ? classes.connected : classes.disconnected
        }`}
      />
    </Tooltip>
  );
};
